<template lang="pug">
    .echart-container
        v-chart(
            :option="chartOption"
            style="height: 200px; width: 100%;"
            autoresize
        )

</template>

<script lang="ts">
    import Vue from 'vue';

    interface Goal {
        goal_type: string;
        current_value: number;
        target_value: number;
        recurring_type: string;
    }

    import { use } from 'echarts/core';
    import { PieChart } from 'echarts/charts';
    import { TooltipComponent, GraphicComponent } from 'echarts/components';
    import { CanvasRenderer } from 'echarts/renderers';
    import VChart from 'vue-echarts';

    use([CanvasRenderer, PieChart, TooltipComponent, GraphicComponent]);

    import { resolveCssVar } from "@/helpers/css";

    export default Vue.extend({
        data() {
            return {
            }
        },
        methods: {
            capitalizeFirstLetter(word) {
                return word.charAt(0).toUpperCase() + word.slice(1);
            },
            getColorForGoalType(goalType: string): string {
                switch (goalType) {
                    case 'inbound_phone_calls':
                        return resolveCssVar('--volie-blue-50', '#64b5f6');
                    case 'outbound_phone_calls':
                        return resolveCssVar('--volie-blue', '#1976d2');
                    case 'new_appointments':
                        return resolveCssVar('--purple', '#9c27b0');
                    case 'same_day_appointments':
                        return resolveCssVar('--dark-blue', '#0d47a1');
                    default:
                        return resolveCssVar('--volie-blue', '#1976d2');
                }
            },
            goalProgressPercentage(goal) {
                if (goal?.current_value > 0) {
                    return (goal.current_value / goal.target_value) * 100.0;
                } else {
                    return 0;
                }
            },
        },
        computed: {
            goalArrayProgressPercentages() {
                if (this.goals?.length > 0) {
                    return this.goals.map(goal => Math.floor(this.goalProgressPercentage(goal)));
                } else {
                    return [];
                }
            },
            avgPercentage() {
                const pcts = this.goalArrayProgressPercentages;
                if (pcts.length === 0) return '0%';
                const avg = pcts.reduce((a, b) => a + b, 0) / pcts.length;
                return avg % 1 === 0 ? avg.toFixed(0) + '%' : avg.toFixed(1) + '%';
            },
            recurringLabel() {
                if (this.goals?.length > 0) {
                    return this.capitalizeFirstLetter(this.goals[0].recurring_type);
                }
                return '';
            },
            chartOption() {
                const pcts = this.goalArrayProgressPercentages;
                const count = this.goals.length;

                const ringWidth = 12;
                const ringGap = 4;
                const outerRadius = 90;

                const series = this.goals.map((goal, i) => {
                    const pct = pcts[i] || 0;
                    const color = this.getColorForGoalType(goal.goal_type);
                    const goalName = goal.goal_type.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

                    const outer = outerRadius - i * (ringWidth + ringGap);
                    const inner = outer - ringWidth;

                    return {
                        type: 'pie',
                        radius: [inner + '%', outer + '%'],
                        center: ['50%', '50%'],
                        startAngle: 90,
                        silent: false,
                        label: { show: false },
                        labelLine: { show: false },
                        emphasis: {
                            scale: false,
                            itemStyle: {
                                shadowBlur: 10,
                                shadowColor: 'rgba(0,0,0,0.2)',
                            },
                        },
                        data: [
                            {
                                value: pct,
                                name: goalName,
                                itemStyle: {
                                    color: color,
                                    borderRadius: 10,
                                },
                            },
                            {
                                value: Math.max(0, 100 - pct),
                                name: '',
                                itemStyle: {
                                    color: '#e8e8e8',
                                },
                                tooltip: { show: false },
                                emphasis: {
                                    disabled: true,
                                },
                            },
                        ],
                    };
                });

                return {
                    tooltip: {
                        trigger: 'item',
                        formatter: (params) => {
                            if (!params.name) return '';
                            return `<b>${params.name}</b>: ${params.value.toFixed(1)}%`;
                        },
                    },
                    series: series,
                    graphic: [
                        {
                            type: 'text',
                            left: 'center',
                            top: '40%',
                            style: {
                                text: this.recurringLabel,
                                fontSize: 13,
                                fontWeight: 'bold',
                                fill: '#333',
                                textAlign: 'center',
                            },
                            z: 10,
                        },
                        {
                            type: 'text',
                            left: 'center',
                            top: '52%',
                            style: {
                                text: this.avgPercentage,
                                fontSize: 16,
                                fontWeight: 'bold',
                                fill: '#333',
                                textAlign: 'center',
                            },
                            z: 10,
                        },
                    ],
                };
            },
        },
        components: {
            VChart,
        },
        props: {
            goals: {
                type: Array as () => Goal[],
                required: true,
            }
        },
        watch: {
        },
        destroyed() {
        },
        created() {
        },
    });
</script>

<style lang="scss" scoped>
</style>
